/**
 * Запуск генерации героини: модели, число вариантов, оценка, подтверждение дорогого.
 *
 * Ключ идемпотентности живёт от ОТВЕТА до ОТВЕТА (как в `useGenerate`): двойной клик
 * и повтор после обрыва уходят с одним ключом и ставят одну оплаченную задачу, новый
 * ключ — только после успешного ответа. Оценка выше $1 требует второго клика: первый
 * переводит кнопку в «Да, запустить за $X» (туда же ведёт и 409 CONFIRM_REQUIRED).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Btn, ErrorLine, Hint, Panel, selectClass } from "@/components/project/ui";
import { defaultChosen, ModelChips } from "@/components/persona/ModelChips";
import {
  estimateRun,
  estimateSourceText,
  estimateText,
  isConfirmRequired,
  launchRebuild,
  launchRun,
  launchSheet,
  type LaunchResult,
  type ModelInfo,
} from "@/lib/persona";
import { errorText, newKey, usd } from "@/lib/project";

export type LaunchMode = "portrait" | "sheet" | "rebuild";

const CONFIRM_ABOVE = 1;
const TITLE: Record<LaunchMode, string> = {
  portrait: "Сгенерировать портрет",
  sheet: "Лист персонажа",
  rebuild: "Пересобрать из канона",
};

export function LaunchPanel({
  pid,
  mode,
  promptId,
  models,
  onLaunched,
  onCancelMode,
}: {
  pid: number;
  mode: LaunchMode;
  promptId: number | null;
  models: ModelInfo[];
  onLaunched: (r: LaunchResult) => void;
  onCancelMode: () => void;
}) {
  const [chosenMap, setChosenMap] = useState(() => defaultChosen(models));
  const [variants, setVariants] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const key = useRef(newKey());

  const chosen = models.filter((m) => chosenMap[m.code] && m.discovered).map((m) => m.code);
  const spec = Object.fromEntries(chosen.map((code) => [code, variants]));
  const chosenKey = chosen.join(",");

  // Другой набор — другая цена: прежнее согласие на сумму больше не действует.
  useEffect(() => setConfirming(false), [chosenKey, variants, mode]);

  const estimate = useQuery({
    queryKey: ["persona-estimate", pid, mode, chosenKey, variants],
    queryFn: () => estimateRun(pid, { models: spec, kind: mode }),
    enabled: chosen.length >= 1,
  });
  const total = estimate.data?.total ?? null;

  const needPrompt = mode !== "sheet" && promptId === null;

  const launch = useMutation({
    mutationFn: (confirm: boolean) => {
      const body = { key: key.current, models: spec, ...(confirm ? { confirm: true } : {}) };
      if (mode === "sheet") return launchSheet(pid, body);
      const withPrompt = { ...body, prompt_id: promptId as number };
      return mode === "rebuild" ? launchRebuild(pid, withPrompt) : launchRun(pid, withPrompt);
    },
    onSuccess: (r) => {
      key.current = newKey();
      setConfirming(false);
      onLaunched(r);
    },
    onError: (e) => {
      if (isConfirmRequired(e)) setConfirming(true);
    },
  });

  const click = () => {
    if (launch.isPending) return;
    if (!confirming && total !== null && total > CONFIRM_ABOVE) {
      setConfirming(true);
      return;
    }
    launch.mutate(confirming);
  };

  const showError = launch.isError && !isConfirmRequired(launch.error);

  return (
    <Panel
      title={TITLE[mode]}
      aside={
        mode !== "portrait" ? (
          <button type="button" className="text-accent-text hover:underline" onClick={onCancelMode}>
            отмена
          </button>
        ) : undefined
      }
      annot="запуск генерации"
    >
      <div className="grid gap-3">
        <ModelChips models={models} value={chosenMap} onChange={setChosenMap} />
        <label className="flex items-center gap-2 text-[12.5px] text-fg-muted">
          вариантов на модель
          <select className={selectClass} value={variants} onChange={(e) => setVariants(Number(e.target.value))}>
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <Btn
            tone="primary"
            onClick={click}
            disabled={launch.isPending || chosen.length === 0 || needPrompt}
            className={confirming ? "bg-warn text-bg" : undefined}
          >
            {launch.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
            {confirming ? `Да, запустить за ${usd(total, 2)}` : "Сгенерировать"}
          </Btn>
          <span className="text-[12.5px] text-fg-muted tnum">
            {chosen.length === 0
              ? "выберите модель"
              : total === null
                ? estimate.isError
                  ? "оценка недоступна"
                  : "считаю…"
                : `итого ${estimateText(total, estimate.data?.source === "fact" ? "fact" : "spike")}${estimateSourceText(
                    estimate.data?.source,
                  )}`}
          </span>
        </div>
        {needPrompt ? <Hint>Нужна версия промпта: составьте или выберите её выше.</Hint> : null}
        {confirming ? <Hint>Дороже ${CONFIRM_ABOVE}: нажмите ещё раз, чтобы подтвердить.</Hint> : null}
        {launch.data?.reused ? <Hint>такой же запрос уже в работе</Hint> : null}
        <ErrorLine text={showError ? errorText(launch.error) : null} />
        <ErrorLine text={estimate.isError ? errorText(estimate.error) : null} />
      </div>
    </Panel>
  );
}
